'use client';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import ListingCard from '@/components/ListingCard';
import { loadListingsPage } from '@/app/actions/listing-results';
import type { CardData } from '@/lib/listing-card';
import { GRID_CLASS, LOAD_MORE_TIMES, RESULTS_PAGE_SIZE, pageHref, pageNumbers, totalPages } from '@/lib/results-paging';
import { numberFormat } from '@/lib/format';

// A list of listings (listing browse plan, 2026-09-23): the grid, one
// "Show 24 more" button under it, and the page numbers.
//
// The first page arrives rendered from the server (children). Each "Show
// more" adds the next page below it; after five the button goes and the page
// numbers carry on. The address does not change as the list grows - Back from
// a listing puts the grown list back instead, from this tab's session storage,
// and brings the card that was opened back into view.

// Back and Forward, told apart from a click on a link: a client-side Back
// fires popstate just before the list mounts again; a Back that reloads the
// document says so in its navigation entry.
let lastPopAt = 0;
let documentHadList = false;
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    lastPopAt = Date.now();
  });
}
function cameBack(): boolean {
  if (Date.now() - lastPopAt < 3000) return true;
  if (documentHadList) return false;
  const nav = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
  return nav?.type === 'back_forward';
}

type Saved = { firstPage: number; lastPage: number; rows: CardData[]; cardId: string | null; at: number };
const KEEP_MS = 30 * 60 * 1000;

function readSaved(key: string): Saved | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Saved) : null;
  } catch {
    return null;
  }
}
function writeSaved(key: string, saved: Saved) {
  try {
    sessionStorage.setItem(key, JSON.stringify(saved));
  } catch {
    // Storage full or blocked: Back then shows the first page, nothing breaks.
  }
}

function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div aria-hidden="true" className={`rounded-2xl overflow-hidden border border-neutral-800 bg-neutral-900 ${className}`}>
      <div className="aspect-[3/2] bg-neutral-800 motion-safe:animate-pulse" />
      <div className="p-4 space-y-2">
        <div className="h-6 w-1/2 rounded bg-neutral-800 motion-safe:animate-pulse" />
        <div className="h-4 w-3/4 rounded bg-neutral-800 motion-safe:animate-pulse" />
        <div className="h-4 w-2/3 rounded bg-neutral-800 motion-safe:animate-pulse" />
      </div>
    </div>
  );
}

export default function ResultsGrid({
  lang,
  children,
  page,
  total,
  firstIds,
  params,
  agencyId = null,
  listPath,
  linkParams,
  hideSeller = false,
  paging = true,
}: {
  lang: string;
  /** The first page's cards, rendered on the server. */
  children: React.ReactNode;
  page: number;
  total: number;
  /** Ids already on the first page, so a listing added meanwhile is not shown twice. */
  firstIds: string[];
  /** The search as the server ran it, for the next pages. */
  params: Record<string, string>;
  agencyId?: string | null;
  /** Where the page numbers point, and the visitor's own query parameters. */
  listPath: string;
  linkParams: Record<string, unknown>;
  hideSeller?: boolean;
  /** Off for a fixed list (similar listings): no "Show more", no numbers. */
  paging?: boolean;
}) {
  const isFr = lang === 'fr';
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const key = `results:${pathname}?${searchParams?.toString() || ''}`;

  const [extra, setExtra] = useState<CardData[]>([]);
  const [lastPage, setLastPage] = useState(page);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [announce, setAnnounce] = useState('');
  const [now, setNow] = useState(0);
  const [pending, setPending] = useState(false);
  const focusId = useRef<string | null>(null);
  const scrollToId = useRef<string | null>(null);

  // Back from a listing: the list as it was, before the first paint.
  useLayoutEffect(() => {
    const saved = readSaved(key);
    const back = cameBack();
    documentHadList = true;
    if (!saved || !back || Date.now() - saved.at > KEEP_MS || saved.firstPage !== page) return;
    if (saved.rows.length) {
      setNow(Date.now());
      setExtra(saved.rows);
      setLastPage(saved.lastPage);
    }
    scrollToId.current = saved.cardId;
  }, [key, page]);

  useEffect(() => {
    const id = scrollToId.current;
    if (id) {
      scrollToId.current = null;
      requestAnimationFrame(() => {
        const card = document.getElementById(id);
        if (!card) return;
        const r = card.getBoundingClientRect();
        if (r.top < 80 || r.bottom > window.innerHeight) card.scrollIntoView({ block: 'center' });
      });
    }
    const focus = focusId.current;
    if (focus) {
      focusId.current = null;
      document.querySelector<HTMLAnchorElement>(`#listing-${CSS.escape(focus)} h3 a`)?.focus();
    }
  }, [extra]);

  // Another search on its way (a filter, a chip, the sort): the list dims until
  // it arrives (components/filters/use-results-navigation.ts).
  useEffect(() => {
    const onPending = (e: Event) => setPending(!!(e as CustomEvent).detail);
    window.addEventListener('search-pending', onPending);
    return () => window.removeEventListener('search-pending', onPending);
  }, []);

  const remember = (cardId: string | null, rows = extra, last = lastPage) => {
    writeSaved(key, { firstPage: page, lastPage: last, rows, cardId, at: Date.now() });
  };

  const loadMore = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const next = lastPage + 1;
      const res = await loadListingsPage(params, agencyId, next);
      const seen = new Set([...firstIds, ...extra.map((r) => r.id)]);
      const fresh = res.rows.filter((r) => !seen.has(r.id));
      const rows = [...extra, ...fresh];
      setNow(Date.now());
      setExtra(rows);
      setLastPage(next);
      remember(null, rows, next);
      focusId.current = fresh[0]?.id ?? null;
      const shown = Math.min(total, next * RESULTS_PAGE_SIZE);
      setAnnounce(isFr ? `${fresh.length} annonces de plus, ${shown} sur ${total}` : `${fresh.length} more listings shown, ${shown} of ${total}`);
    } catch {
      setFailed(true);
    }
    setLoading(false);
  };

  const pages = totalPages(total);
  const shownTo = Math.min(total, lastPage * RESULTS_PAGE_SIZE);
  const shownFrom = (page - 1) * RESULTS_PAGE_SIZE + 1;
  const remaining = total - shownTo;
  const canLoadMore = paging && remaining > 0 && lastPage - page < LOAD_MORE_TIMES;
  const num = (n: number) => numberFormat(isFr ? 'fr-CA' : 'en-CA').format(n);
  const href = (p: number) => pageHref(listPath, linkParams, p);

  return (
    <div>
      <div
        className={`${GRID_CLASS} transition-opacity duration-200 motion-reduce:transition-none ${pending ? 'opacity-40 pointer-events-none' : ''}`}
        aria-busy={pending}
        id="property-grid-container"
        // The card a visitor opens is the one Back brings them to.
        onClickCapture={(e) => {
          const card = (e.target as Element).closest('article[id^="listing-"]');
          if (card) remember(card.id);
        }}
      >
        {children}
        {extra.map((row) => (
          <ListingCard key={row.id} property={row} lang={lang} now={now} hideSeller={hideSeller} className="render-later-card " />
        ))}
        {loading && (
          <>
            <Skeleton />
            <Skeleton className="hidden min-[600px]:block" />
            <Skeleton className="hidden lg:block" />
            <Skeleton className="hidden min-[1440px]:block" />
          </>
        )}
      </div>

      <p role="status" className="sr-only">{announce}</p>

      {paging && total > 0 && (
        <div className="mt-8 flex flex-col items-center gap-4">
          {/* "48 of 3,214" only while there is more: with everything shown it
              would only repeat the count over the list. */}
          {(remaining > 0 || page > 1) && (
          <p className="text-sm text-neutral-400">
            {page === 1
              ? (isFr ? `${num(shownTo)} sur ${num(total)} annonces` : `${num(shownTo)} of ${num(total)} listings`)
              : (isFr ? `${num(shownFrom)}–${num(shownTo)} sur ${num(total)} annonces` : `${num(shownFrom)}–${num(shownTo)} of ${num(total)} listings`)}
          </p>
          )}

          {canLoadMore && (
            <button
              type="button"
              onClick={loadMore}
              disabled={loading}
              aria-disabled={loading}
              className="w-full sm:w-auto min-w-64 h-12 px-6 rounded-xl bg-gold text-black font-bold text-base flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70"
            >
              {loading && <Loader2 className="w-4 h-4 motion-safe:animate-spin" />}
              {loading
                ? (isFr ? 'Chargement…' : 'Loading…')
                : (isFr ? `Afficher ${Math.min(RESULTS_PAGE_SIZE, remaining)} de plus` : `Show ${Math.min(RESULTS_PAGE_SIZE, remaining)} more`)}
            </button>
          )}
          {failed && (
            <p role="alert" className="text-sm text-danger">
              {isFr ? 'Le chargement a échoué. Réessayez.' : 'Could not load more. Please try again.'}
            </p>
          )}

          {pages > 1 && (
            <nav aria-label={isFr ? 'Pages de résultats' : 'Result pages'} className="w-full flex items-center justify-between sm:justify-center gap-2">
              {page > 1 ? (
                <Link href={href(page - 1)} rel="prev" className="h-11 px-3 rounded-xl flex items-center gap-1 text-sm font-semibold text-neutral-100 border border-neutral-800">
                  <ChevronLeft className="w-4 h-4" />
                  {isFr ? 'Précédente' : 'Previous'}
                </Link>
              ) : <span className="w-[6.5rem] sm:hidden" />}

              {/* A phone gets "Page 3 of 134"; a wider screen the numbers. */}
              <span className="sm:hidden text-sm text-neutral-300">
                {isFr ? `Page ${num(lastPage)} sur ${num(pages)}` : `Page ${num(lastPage)} of ${num(pages)}`}
              </span>
              <ul className="hidden sm:flex items-center gap-1">
                {pageNumbers(lastPage, pages).map((p, i) =>
                  p === null ? (
                    <li key={`gap-${i}`} aria-hidden="true" className="w-8 text-center text-neutral-400">…</li>
                  ) : (
                    <li key={p}>
                      {p === lastPage ? (
                        <span aria-current="page" className="min-w-11 h-11 px-2 rounded-xl flex items-center justify-center text-sm font-bold bg-white text-black">
                          {num(p)}
                        </span>
                      ) : (
                        <Link href={href(p)} className="min-w-11 h-11 px-2 rounded-xl flex items-center justify-center text-sm font-semibold text-neutral-200 hover:bg-neutral-800">
                          {num(p)}
                        </Link>
                      )}
                    </li>
                  )
                )}
              </ul>

              {lastPage < pages ? (
                <Link href={href(lastPage + 1)} rel="next" className="h-11 px-3 rounded-xl flex items-center gap-1 text-sm font-semibold text-neutral-100 border border-neutral-800">
                  {isFr ? 'Suivante' : 'Next'}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              ) : <span className="w-[6.5rem] sm:hidden" />}
            </nav>
          )}
        </div>
      )}
    </div>
  );
}
